import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { CompanyDashboardSummary } from '@/api/types';
import { Banner, Card, ErrorNote, Money, Reference, Spinner, Timestamp } from '@/components/Common';
import { StatusPill } from '@/components/StatusPill';
import { money, percent } from '@/lib/format';

/**
 * A company's own "/" — everything the platform admin dashboard shows, scoped to this merchant
 * alone via GET /api/v1/dashboard/summary (DashboardController), plus the one thing that matters
 * most to a pay-on-account merchant and that the admin dashboard has no reason to show: how much
 * of today's limit is left.
 *
 * Layout follows an F-pattern: a compact header, then a stat strip with today's headline numbers
 * (orders, success rate, numbers saved, limit status) above the fold, then recent activity —
 * promoted up because it's the highest-frequency thing anyone checks this screen for — then the
 * detail cards (this month, today-by-currency, limit breakdown, saved numbers) below.
 */
export function CompanyDashboardPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['company-dashboard'],
    queryFn: () => api.get<CompanyDashboardSummary>('/api/v1/dashboard/summary'),
    refetchInterval: 15_000,
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;
  if (!data) return null;

  const todayOrders = data.today.reduce((sum, row) => sum + row.orderCount, 0);
  const rate = data.successRate24h.rate == null ? null : Number(data.successRate24h.rate);
  const rateTone = rate === null ? 'text-ink-400'
    : rate >= 0.95 ? 'text-emerald-700' : rate >= 0.85 ? 'text-amber-700' : 'text-red-700';

  const limitFractions = data.limits.dailyLimit == null ? [] : data.limits.spentTodayByCurrency
    .map((row) => ({ currency: row.currency, fraction: Number(row.amount) / Number(data.limits.dailyLimit) }))
    .filter((row) => row.fraction > 0);
  const worstFraction = Math.max(0, ...limitFractions.map((f) => f.fraction));
  const limitTone = worstFraction >= 1 ? 'text-red-700' : worstFraction >= 0.8 ? 'text-amber-700' : 'text-emerald-700';

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-end">
        <Link to="/recharges/new" className="btn-primary">New recharge</Link>
      </div>

      {data.openExceptions > 0 && (
        <Banner tone="warn">
          <strong>{data.openExceptions}</strong> of your order{data.openExceptions === 1 ? '' : 's'}
          {' '}need{data.openExceptions === 1 ? 's' : ''} attention — an unexpected outcome is being
          looked into. Contact support if this doesn't clear on its own.
        </Banner>
      )}
      {worstFraction >= 1 && (
        <Banner tone="danger">
          <strong>Today's limit is reached</strong> on at least one currency — further recharges
          in that currency will be declined until it resets tomorrow.
        </Banner>
      )}
      {worstFraction >= 0.8 && worstFraction < 1 && (
        <Banner tone="warn">
          <strong>Approaching today's limit</strong> — {Math.round(worstFraction * 100)}% used so far.
        </Banner>
      )}

      {/* Stat strip — the four numbers worth a glance without scrolling. */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile label="Orders today" value={String(todayOrders)} />
        <StatTile label="Success rate (24h)" value={percent(data.successRate24h.rate)} tone={rateTone} />
        <StatTile label="Numbers saved" value={String(data.managedNumbers.active)}
                  sub={`${data.managedNumbers.total} total`} />
        <StatTile label="Today's limit"
                  value={limitFractions.length === 0 ? '—' : `${Math.round(worstFraction * 100)}%`}
                  tone={limitFractions.length === 0 ? 'text-ink-400' : limitTone} />
      </div>

      <Card title="Recent orders" actions={<Link to="/orders" className="text-sm text-ink-600 hover:underline">View all</Link>}>
        <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-ink-200">
            <tr>
              <th className="th">Reference</th>
              <th className="th">Status</th>
              <th className="th">Target</th>
              <th className="th text-right">Amount</th>
              <th className="th">Placed</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {data.recentOrders.map((order) => (
              <tr key={order.id} className="hover:bg-ink-50">
                <td className="td">
                  <Link to={`/orders/${order.id}`} className="hover:underline">
                    <Reference value={order.agentReference} />
                  </Link>
                </td>
                <td className="td"><StatusPill status={order.status} /></td>
                <td className="td mono">{order.target}</td>
                <td className="td text-right">
                  <Money amount={money(order.amount)} currency={order.currency} />
                </td>
                <td className="td"><Timestamp value={order.createdAt} /></td>
              </tr>
            ))}
            {data.recentOrders.length === 0 && (
              <tr><td className="td py-8 text-center" colSpan={5}>
                <p className="text-sm text-ink-500">No orders yet.</p>
                <Link to="/recharges/new" className="mt-2 inline-block text-sm font-medium text-ink-700 underline">
                  Place your first recharge →
                </Link>
              </td></tr>
            )}
          </tbody>
        </table>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <LimitCard limits={data.limits} />
        <Card title="This month">
          {data.thisMonth.length === 0 ? (
            <div className="p-4 text-center">
              <p className="text-sm text-ink-500">Nothing placed yet this month.</p>
              <Link to="/recharges/new" className="mt-2 inline-block text-sm font-medium text-ink-700 underline">
                Start a recharge →
              </Link>
            </div>
          ) : (
            <dl className="divide-y divide-ink-100">
              {data.thisMonth.map((row) => (
                <div key={row.currency} className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-sm text-ink-700">
                    {row.currency} — {row.orderCount} order{row.orderCount === 1 ? '' : 's'}
                  </span>
                  <span className="tabular text-sm font-semibold text-ink-900">
                    <Money amount={money(row.totalValue)} currency={row.currency} />
                  </span>
                </div>
              ))}
            </dl>
          )}
        </Card>
      </div>

      <NumbersCard managedNumbers={data.managedNumbers} />

      {data.wallets && data.wallets.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {data.wallets.map((wallet) => (
            <div key={wallet.accountTypeId} className="card p-4">
              <span className="label">{wallet.name}</span>
              <div className="tabular mt-2 text-2xl font-semibold text-ink-900">
                {money(wallet.balance)}
              </div>
              <div className="mt-1 text-xs text-ink-500">
                as at <Timestamp value={wallet.capturedAt} />
              </div>
            </div>
          ))}
        </div>
      )}

      <Card title="Today by currency">
        <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-ink-200">
            <tr>
              <th className="th">Currency</th>
              <th className="th text-right">Orders</th>
              <th className="th text-right">Succeeded</th>
              <th className="th text-right">Value</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {data.today.map((row) => (
              <tr key={row.currency}>
                <td className="td font-medium">{row.currency}</td>
                <td className="td tabular text-right">{row.orderCount}</td>
                <td className="td tabular text-right">{row.succeeded}</td>
                <td className="td tabular text-right">
                  <Money amount={money(row.totalValue)} currency={row.currency} />
                </td>
              </tr>
            ))}
            {data.today.length === 0 && (
              <tr><td className="td text-ink-500" colSpan={4}>Nothing placed today yet.</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </Card>
    </div>
  );
}

function StatTile({ label, value, sub, tone = 'text-ink-900' }: {
  label: string; value: string; sub?: string; tone?: string;
}) {
  return (
    <div className="card p-4">
      <div className="label">{label}</div>
      <div className={`tabular mt-1 text-2xl font-semibold ${tone}`}>{value}</div>
      {sub && <div className="mt-0.5 text-xs text-ink-500">{sub}</div>}
    </div>
  );
}

function LimitCard({ limits }: { limits: CompanyDashboardSummary['limits'] }) {
  return (
    <Card title="Today's limit">
      {limits.dailyLimit == null ? (
        <p className="p-4 text-sm text-ink-500">No daily limit is set on this account.</p>
      ) : (
        <div className="space-y-3 p-4">
          {limits.spentTodayByCurrency
            .filter((row) => Number(row.amount) > 0 || limits.spentTodayByCurrency.length === 1)
            .map((row) => {
              const spent = Number(row.amount);
              const limit = Number(limits.dailyLimit);
              const fraction = limit > 0 ? Math.min(1, spent / limit) : 0;
              const tone = fraction >= 1 ? 'bg-red-600' : fraction >= 0.8 ? 'bg-amber-500' : 'bg-emerald-600';
              return (
                <div key={row.currency}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-ink-600">{row.currency}</span>
                    <span className="tabular font-semibold text-ink-900">
                      {money(row.amount)} / {money(limits.dailyLimit)}
                    </span>
                  </div>
                  <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-ink-100">
                    <div className={`h-full rounded-full ${tone}`} style={{ width: `${fraction * 100}%` }} />
                  </div>
                </div>
              );
            })}
        </div>
      )}
      {limits.perTxnLimit != null && (
        <p className="border-t border-ink-100 px-4 py-2 text-xs text-ink-500">
          Per-transaction limit: {money(limits.perTxnLimit)}
        </p>
      )}
    </Card>
  );
}

function NumbersCard({ managedNumbers }: { managedNumbers: CompanyDashboardSummary['managedNumbers'] }) {
  return (
    <Card
      title="Your numbers"
      actions={<Link to="/numbers" className="text-sm text-ink-600 hover:underline">Manage</Link>}
    >
      {managedNumbers.total === 0 ? (
        <div className="p-4 text-center">
          <p className="text-sm text-ink-500">No numbers saved yet.</p>
          <Link to="/numbers" className="mt-2 inline-block text-sm font-medium text-ink-700 underline">
            Add the ones you recharge regularly →
          </Link>
        </div>
      ) : managedNumbers.recentlyRecharged.length === 0 ? (
        <p className="p-4 text-sm text-ink-500">None of your saved numbers have been recharged yet.</p>
      ) : (
        <dl className="divide-y divide-ink-100">
          {managedNumbers.recentlyRecharged.map((row) => (
            <div key={row.target} className="flex items-center justify-between px-4 py-2.5">
              <div>
                <div className="text-sm font-medium text-ink-800">{row.label}</div>
                <div className="mono text-xs text-ink-500">{row.target}</div>
              </div>
              <div className="flex items-center gap-2">
                <StatusPill status={row.lastRechargeStatus} />
                <Timestamp value={row.lastRechargeAt} />
              </div>
            </div>
          ))}
        </dl>
      )}
    </Card>
  );
}
